<script lang="ts">
    import {onMount, tick} from 'svelte';

    let {onchange, size, children} = $props();

    const startColor = size === 'large' ? '#f00020' : '#e5e5e5';
    const endColor = '#007268';

    const hexToRGB = (hex: string) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);

        return {r, g, b};
    }

    const rgbStartColor = hexToRGB(startColor);
    const rgbEndColor = hexToRGB(endColor);

    let maxMovedBy = $state(0);

    let isDragging = $state(false);
    let startPos = $state(0);
    let movedBy = $state(0);

    let isLoading = $state(false);

    let buttonEl: HTMLButtonElement;
    let handleEl: HTMLSpanElement;
    let labelContainerEl: HTMLSpanElement;

    export const reset = () => {
        startPos = 0;
        movedBy = 0;
        isDragging = false;
        buttonEl.style.backgroundColor = startColor;
        handleEl.style.transform = '';
        labelContainerEl.style.opacity = '1';
        isLoading = false;
    }

    onMount(async () => {
        /*                                                   */
        await tick();

        buttonEl.style.backgroundColor = startColor;
    })

    const startTouch = (event: PointerEvent) => {
        const currentPosition = event.pageX;

        maxMovedBy = buttonEl.offsetWidth - handleEl.offsetWidth - 10;

        const elementPosition = handleEl!.getBoundingClientRect().left;
        const minPosition = elementPosition;
        const maxPosition = handleEl.offsetWidth + elementPosition;

        const isOutOfArea = currentPosition < minPosition || currentPosition > maxPosition;

        if (buttonEl!.disabled || isOutOfArea || isLoading) {
            return;
        }

        startPos = currentPosition;
        movedBy = 0;
        isDragging = true;
    }

    const moveTouch = (event: PointerEvent) => {
        event.preventDefault();

        if (!isDragging) {
            return
        }

        const currentXPosition = event.pageX;
        movedBy = Math.max(Math.min(currentXPosition - startPos, maxMovedBy), 0);

        const movedByPercent = movedBy / maxMovedBy;
        buttonEl!.style.backgroundColor = getColorByPercent(movedByPercent);

        handleEl.style.transform = `translate(${movedBy}px)`;
        handleEl.style.transition = 'unset'; /*                                        */

        if (size !== "small") {
            return
        }

        labelContainerEl.style.opacity = `${1 - movedByPercent}`;
    }

    const endTouch = (event: PointerEvent | MouseEvent) => {
        const movedByPercent = movedBy / maxMovedBy;

        /*             */
        if (event.type === 'mouseleave' && movedByPercent === 0) {
            return
        }

        /*         */
        if ((!isDragging && movedByPercent !== 0) || isLoading) {
            return;
        }

        isDragging = false;
        handleEl.style.transition = '';

        if (movedByPercent < 0.5 && movedByPercent !== 0.0) {
            reset();
            return;
        }

        applyActivationStyling();
        onchange();
    }

    const applyActivationStyling = () => {
        if (size === "small") {
            labelContainerEl.style.opacity = '0';
        }

        isLoading = true;
        handleEl.style.transform = `translate(${maxMovedBy}px)`;
        buttonEl!.style.backgroundColor = endColor;
    }

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Enter' || isLoading) {
            return;
        }

        maxMovedBy = buttonEl.offsetWidth - handleEl.offsetWidth - 10;
        applyActivationStyling();
        onchange();
    }

    const getColorByPercent = (movedByPercent: number): string => {
        if (movedByPercent > 1) {
            return endColor;
        }
        if (movedByPercent < 0) {
            return startColor;
        }

        function rgbToHex(r: number, g: number, b: number): string {
            return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        }

        const w2 = 1 - movedByPercent;
        return rgbToHex(
            Math.round(rgbEndColor.r * movedByPercent + rgbStartColor.r * w2),
            Math.round(rgbEndColor.g * movedByPercent + rgbStartColor.g * w2),
            Math.round(rgbEndColor.b * movedByPercent + rgbStartColor.b * w2),
        );
    }
</script>

<button class="slider-wrapper"
        data-testid="touch-slider-button"
        role="switch"
        aria-checked="false"
        onkeydown={onKeyDown}
        onpointerdown={startTouch}
        onpointerup={endTouch}
        onmouseleave={endTouch}
        onpointermove={moveTouch}
        aria-disabled={isLoading}
        bind:this={buttonEl}>
    <span class="slider-handle"
          data-testid="touch-slider-handle"
          bind:this={handleEl}></span>
    <span bind:this={labelContainerEl} class="slider-label">
        {@render children()}
    </span>
    <oc-spinner-v1 size={size === "large" ? "100" : "50"}
                   variant="default"
                   style:visibility={isLoading ? "visible" : "hidden"}
                   class="slider-loader"></oc-spinner-v1>
</button>